import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import type { JourneyMarkState } from '@src/models/interfaces/attendanceEvent.interface';
import {
  buildAttendanceJourneyTimeline,
  getScheduledAtByTypeFromScheduleDetails,
} from '@src/models/mappers/attendanceJourneyTimeline';
import type { UserListSearchField } from '@src/models/mappers/UserMapper';
import { DateUtil } from '@src/packages/utils/dates';
import { useDebouncedValue } from '@src/packages/utils/hooks';
import {
  ATTENDANCE_EVENT_ACTOR_ROLE,
  ATTENDANCE_EVENT_TYPE,
} from '@src/packages/workJourney/constants';
import AttendanceEventService from '@src/services/AttendanceEventService';
import UserService from '@src/services/UserService';
import UserWorkJourneyScheduleService from '@src/services/UserWorkJourneyScheduleService';
import { formatUserFullName } from '@src/util/format/userFullName';
import { userHasAttendanceAddress } from '@src/packages/users/screens/UsersList/utils/userHasAttendanceAddress';

import type {
  MarkAttendanceConfirmKind,
  MarkAttendanceJourneyDetails,
  MarkAttendanceSelectedUser,
  MarkAttendanceStep,
  MarkAttendanceUserRow,
} from '../types';
import { clampMarkedAtToNow } from '../utils/clampMarkedAtToNow';
import {
  mapNewAttendanceEventError,
  NEW_ATTENDANCE_EVENT_ERROR_MESSAGE_ID,
} from '../utils/mapNewAttendanceEventError';

type UseMarkAttendanceDrawerParams = {
  open: boolean;
  /** When provided, the drawer preselects this user and jumps to step 2. */
  initialUser?: MarkAttendanceUserRow | null;
  /** When provided, the first step is restricted to these journey members. */
  initialUsers?: MarkAttendanceUserRow[] | null;
  /**
   * Today's journey name by employee id (from attendance dashboard).
   * Used to label Elastic org-user rows that are not scoped to a journey.
   */
  journeyNameByEmployeeId?: Record<string, string> | null;
  /** User-list pick when the daily journey has not started — parent opens the start modal. */
  onStartJourneyInstead: (user: MarkAttendanceUserRow) => void;
};

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

/** Elastic fields matched by the Mark Attendance search box (server-side). */
const MARK_ATTENDANCE_USER_SEARCH_FIELDS: UserListSearchField[] = [
  'firstName',
  'lastName',
  'email',
  'idCard',
];

const toSelectedUser = (user: MarkAttendanceUserRow): MarkAttendanceSelectedUser | null => {
  if (!user?.id) {
    return null;
  }
  const fromParts = formatUserFullName(user);
  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    // Prefer first+last; keep Elastic label when parts are missing.
    fullName: fromParts || user.fullName?.trim() || undefined,
    email: user.email,
    idCard: user.idCard,
    photoURL: user.photoURL,
    workJourneyConfigId: user.info?.workJourneyConfigId ?? null,
    workShiftId: user.info?.workShiftId ?? null,
  };
};

const toMarkAttendanceUserRow = (user: {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  idCard?: string;
  photoURL?: string | null;
}): MarkAttendanceUserRow => ({
  id: user.id,
  firstName: user.firstName,
  lastName: user.lastName,
  email: user.email,
  idCard: user.idCard,
  photoURL: user.photoURL ?? undefined,
  journeyName: null,
});

const withJourneyName = (
  user: MarkAttendanceUserRow,
  journeyNameByEmployeeId?: Record<string, string> | null,
): MarkAttendanceUserRow => {
  if (user.journeyName?.trim()) {
    return user;
  }

  const employeeId = user.id?.trim() ?? '';
  const fromMap = employeeId ? journeyNameByEmployeeId?.[employeeId] ?? null : null;
  if (user.journeyName === fromMap) {
    return user;
  }

  return { ...user, journeyName: fromMap };
};

const EMPTY_JOURNEY_MARK_STATE: JourneyMarkState = {
  hasShiftStart: false,
  isOnBreak: false,
  hasBreakEnd: false,
  hasShiftEnd: false,
  types: [],
  completedMarkings: [],
};

const isJourneyStarted = (
  markState: JourneyMarkState | null,
  details: MarkAttendanceJourneyDetails | null,
): boolean => Boolean(markState?.hasShiftStart) || Boolean(details?.actualStartAtSeconds);

export const useMarkAttendanceDrawer = ({
  open,
  initialUser,
  initialUsers,
  journeyNameByEmployeeId = null,
  onStartJourneyInstead,
}: UseMarkAttendanceDrawerParams) => {
  const intl = useIntl();
  const orgId = useSelector(
    (state: { organizations: { organization: { id: string } } }) =>
      state.organizations.organization.id,
  );
  const actorId = useSelector(
    (state: { user?: { profile?: { data?: { id?: string } } } }) =>
      state.user?.profile?.data?.id ?? '',
  );

  const [step, setStep] = useState<MarkAttendanceStep>(1);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<MarkAttendanceSelectedUser | null>(null);
  const [journeyDetails, setJourneyDetails] = useState<MarkAttendanceJourneyDetails | null>(null);
  const [journeyDetailsLoading, setJourneyDetailsLoading] = useState(false);
  const [journeyDetailsError, setJourneyDetailsError] = useState<string | null>(null);
  /** Mark/break CTA state from `attendance_events` (not denormalized record fields). */
  const [journeyMarkState, setJourneyMarkState] = useState<JourneyMarkState | null>(null);

  const [selectingUser, setSelectingUser] = useState(false);
  const [users, setUsers] = useState<MarkAttendanceUserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const onStartJourneyInsteadRef = useRef(onStartJourneyInstead);
  onStartJourneyInsteadRef.current = onStartJourneyInstead;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [serverTotalCount, setServerTotalCount] = useState(0);
  /** Search term actually sent to Elastic (debounced) — kept with page so they reset together. */
  const [listSearch, setListSearch] = useState('');

  const [confirmKind, setConfirmKind] = useState<MarkAttendanceConfirmKind | null>(null);
  const [confirmMarkedAt, setConfirmMarkedAt] = useState<Date | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  /** Local success after a mark; drawer refresh of journey details comes later. */
  const [markSuccessKind, setMarkSuccessKind] = useState<MarkAttendanceConfirmKind | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  const scopedUsers = useMemo(() => {
    if (!initialUsers) {
      return null;
    }

    const normalizedSearch = listSearch.toLocaleLowerCase();
    if (!normalizedSearch) {
      return initialUsers;
    }

    return initialUsers.filter(user =>
      [user.firstName, user.lastName, user.fullName, user.email, user.idCard, user.journeyName].some(
        value => value?.toLocaleLowerCase().includes(normalizedSearch),
      ),
    );
  }, [initialUsers, listSearch]);

  const visibleUsers = useMemo(() => {
    const pageUsers = scopedUsers
      ? scopedUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : users;

    return pageUsers.map(user => withJourneyName(user, journeyNameByEmployeeId));
  }, [currentPage, journeyNameByEmployeeId, pageSize, scopedUsers, users]);

  const totalCount = scopedUsers?.length ?? serverTotalCount;

  const scheduleServiceRef = useRef<UserWorkJourneyScheduleService | null>(null);
  if (scheduleServiceRef.current === null) {
    scheduleServiceRef.current = new UserWorkJourneyScheduleService();
  }
  const scheduleService = scheduleServiceRef.current;

  const userServiceRef = useRef<UserService | null>(null);
  if (userServiceRef.current === null) {
    userServiceRef.current = new UserService();
  }
  const userService = userServiceRef.current;

  const attendanceEventServiceRef = useRef<AttendanceEventService | null>(null);
  if (attendanceEventServiceRef.current === null) {
    attendanceEventServiceRef.current = new AttendanceEventService();
  }
  const attendanceEventService = attendanceEventServiceRef.current;

  const resetConfirmState = useCallback(() => {
    setConfirmKind(null);
    setConfirmMarkedAt(null);
    setConfirmSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setSearch('');
      setSelectedUser(null);
      setJourneyDetails(null);
      setJourneyDetailsError(null);
      setJourneyDetailsLoading(false);
      setJourneyMarkState(null);
      setUsers([]);
      setServerTotalCount(0);
      setCurrentPage(1);
      setListSearch('');
      setMarkSuccessKind(null);
      setSelectingUser(false);
      resetConfirmState();
    }
  }, [open, resetConfirmState]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setListSearch(debouncedSearch);
    setCurrentPage(1);
  }, [debouncedSearch, open]);

  useEffect(() => {
    if (!open || !orgId || initialUsers) {
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      setUsersLoading(true);
      try {
        const result = (await userService.listElastic({
          orgId,
          pageSize,
          from: (currentPage - 1) * pageSize,
          searchTerm: listSearch,
          searchFields: MARK_ATTENDANCE_USER_SEARCH_FIELDS,
          state: 'enabled',
        })) as { users?: MarkAttendanceUserRow[]; total?: number } | null;

        if (cancelled) {
          return;
        }

        setUsers((result?.users ?? []).map(toMarkAttendanceUserRow));
        setServerTotalCount(result?.total ?? 0);
      } catch (error) {
        console.error('Error fetching users for mark attendance:', error);
        if (!cancelled) {
          setUsers([]);
          setServerTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [open, orgId, currentPage, pageSize, listSearch, userService, initialUsers]);

  const fetchJourneySnapshot = useCallback(
    async (
      userId: string,
    ): Promise<{
      details: MarkAttendanceJourneyDetails | null;
      markState: JourneyMarkState | null;
    }> => {
      if (!orgId || !userId) {
        return { details: null, markState: null };
      }

      const details = await scheduleService.getScheduleDetails(orgId, userId);
      if (!details?.attendanceRecordId) {
        return { details, markState: null };
      }

      const markState = await attendanceEventService.getJourneyMarkState(
        orgId,
        details.attendanceRecordId,
      );
      return { details, markState };
    },
    [attendanceEventService, orgId, scheduleService],
  );

  const applyJourneySnapshot = useCallback(
    (details: MarkAttendanceJourneyDetails | null, markState: JourneyMarkState | null) => {
      setJourneyDetails(details);
      setJourneyMarkState(markState);
      setJourneyDetailsError(null);
    },
    [],
  );

  const loadJourneyDetails = useCallback(
    async (userId: string) => {
      if (!orgId || !userId) {
        setJourneyDetails(null);
        setJourneyMarkState(null);
        return;
      }

      setJourneyDetailsLoading(true);
      setJourneyDetailsError(null);
      setJourneyMarkState(null);
      try {
        const { details, markState } = await fetchJourneySnapshot(userId);
        applyJourneySnapshot(details, markState);
      } catch (error) {
        setJourneyDetails(null);
        setJourneyMarkState(null);
        setJourneyDetailsError(error instanceof Error ? error.message : String(error));
      } finally {
        setJourneyDetailsLoading(false);
      }
    },
    [applyJourneySnapshot, fetchJourneySnapshot, orgId],
  );

  const handleSelectUser = useCallback(
    async (user: MarkAttendanceUserRow) => {
      const selected = toSelectedUser(user);
      if (!selected || !orgId) {
        return;
      }

      const orgUser = await userService.getUser(orgId, selected.id);
      if (!userHasAttendanceAddress(orgUser?.address)) {
        notification.error({
          message: intl.formatMessage({ id: 'users.select.tooltip.missingAddress' }),
        });
        return;
      }

      setSelectingUser(true);
      setMarkSuccessKind(null);
      try {
        const { details, markState } = await fetchJourneySnapshot(selected.id);
        if (!isJourneyStarted(markState, details)) {
          onStartJourneyInsteadRef.current(user);
          return;
        }

        setSelectedUser(selected);
        applyJourneySnapshot(details, markState);
        setStep(2);
      } catch (error) {
        notification.error({
          message: intl.formatMessage({
            id: 'workJourney.realTime.markAttendance.journeyLoadError',
          }),
        });
        console.error('Error loading journey for mark attendance:', error);
      } finally {
        setSelectingUser(false);
      }
    },
    [applyJourneySnapshot, fetchJourneySnapshot, intl, orgId, userService],
  );

  // Preselect the incoming user and jump straight to step 2 when opened from a row.
  useEffect(() => {
    if (!open || !initialUser) {
      return;
    }
    const selected = toSelectedUser(initialUser);
    if (!selected) {
      return;
    }
    setSelectedUser(selected);
    setMarkSuccessKind(null);
    setStep(2);
    loadJourneyDetails(selected.id);
  }, [open, initialUser, loadJourneyDetails]);

  const handleBack = useCallback(() => {
    if (step === 2) {
      setStep(1);
      setSelectedUser(null);
      setJourneyDetails(null);
      setJourneyDetailsError(null);
      setJourneyDetailsLoading(false);
      setJourneyMarkState(null);
      setMarkSuccessKind(null);
      resetConfirmState();
    }
  }, [step, resetConfirmState]);

  const openConfirmModal = useCallback(
    (kind: MarkAttendanceConfirmKind) => {
      const attendanceRecordId = journeyDetails?.attendanceRecordId;
      if (!attendanceRecordId) {
        notification.error({
          message: intl.formatMessage({
            id: 'workJourney.realTime.markAttendance.error.attendanceRecordNotFound',
          }),
        });
        return;
      }
      if (!actorId) {
        notification.error({
          message: intl.formatMessage({
            id: 'workJourney.realTime.markAttendance.error.permissionDenied',
          }),
        });
        return;
      }
      setConfirmKind(kind);
      setConfirmMarkedAt(new Date());
    },
    [actorId, intl, journeyDetails?.attendanceRecordId],
  );

  const handleStartBreak = useCallback(() => {
    openConfirmModal(ATTENDANCE_EVENT_TYPE.BREAK_START);
  }, [openConfirmModal]);

  const handleEndBreak = useCallback(() => {
    openConfirmModal(ATTENDANCE_EVENT_TYPE.BREAK_END);
  }, [openConfirmModal]);

  const handleEndJourney = useCallback(() => {
    openConfirmModal(ATTENDANCE_EVENT_TYPE.SHIFT_END);
  }, [openConfirmModal]);

  const handleCancelConfirm = useCallback(() => {
    if (confirmSubmitting) {
      return;
    }
    resetConfirmState();
  }, [confirmSubmitting, resetConfirmState]);

  const handleConfirmMark = useCallback(async () => {
    if (!confirmKind || !selectedUser || !orgId || !actorId) {
      return;
    }

    setConfirmSubmitting(true);
    try {
      const markedAt = clampMarkedAtToNow(confirmMarkedAt ?? new Date());
      const attendanceRecordId = journeyDetails?.attendanceRecordId;
      if (!attendanceRecordId) {
        notification.error({
          message: intl.formatMessage({
            id: 'workJourney.realTime.markAttendance.error.attendanceRecordNotFound',
          }),
        });
        resetConfirmState();
        return;
      }

      await attendanceEventService.createAttendanceEvent({
        organizationId: orgId,
        attendanceRecordId,
        employeeId: selectedUser.id,
        attendanceType: confirmKind,
        actorId,
        actorRole: ATTENDANCE_EVENT_ACTOR_ROLE.SUPERVISOR,
        timeZone: DateUtil.getDefaultTimeZone(),
        createdAtDevice: markedAt.getTime(),
      });

      setMarkSuccessKind(confirmKind);
      setJourneyMarkState(prev => {
        const base: JourneyMarkState = prev ?? EMPTY_JOURNEY_MARK_STATE;
        const types = base.types.includes(confirmKind)
          ? base.types
          : [...base.types, confirmKind];
        // Use the clamped picker time so a backdated mark matches the timeline immediately.
        const markedAtMs = markedAt.getTime();
        const prevMarks = base.completedMarkings ?? [];
        // Record the server-accepted mark even if portal FSM sequence would skip it
        // (e.g. SHIFT_END without a break) so timeline and CTAs stay aligned.
        const completedMarkings = prevMarks.some(mark => mark.type === confirmKind)
          ? prevMarks
          : [...prevMarks, { type: confirmKind, at: markedAtMs }];

        if (confirmKind === ATTENDANCE_EVENT_TYPE.SHIFT_START) {
          return { ...base, hasShiftStart: true, types, completedMarkings };
        }
        if (confirmKind === ATTENDANCE_EVENT_TYPE.BREAK_START) {
          return { ...base, hasShiftStart: true, isOnBreak: true, types, completedMarkings };
        }
        if (confirmKind === ATTENDANCE_EVENT_TYPE.BREAK_END) {
          return {
            ...base,
            hasShiftStart: true,
            isOnBreak: false,
            hasBreakEnd: true,
            types,
            completedMarkings,
          };
        }
        if (confirmKind === ATTENDANCE_EVENT_TYPE.SHIFT_END) {
          return {
            ...base,
            hasShiftStart: true,
            isOnBreak: false,
            hasShiftEnd: true,
            types,
            completedMarkings,
          };
        }
        return base;
      });
      resetConfirmState();
      notification.success({
        message: intl.formatMessage({
          id: 'workJourney.realTime.markAttendance.success',
        }),
      });
    } catch (error) {
      const errorKey = mapNewAttendanceEventError(error);
      notification.error({
        message: intl.formatMessage({
          id: NEW_ATTENDANCE_EVENT_ERROR_MESSAGE_ID[errorKey],
        }),
      });
      setConfirmSubmitting(false);
    }
  }, [
    actorId,
    attendanceEventService,
    confirmMarkedAt,
    confirmKind,
    intl,
    journeyDetails?.attendanceRecordId,
    orgId,
    resetConfirmState,
    selectedUser,
  ]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const hasJourneyStarted =
    isJourneyStarted(journeyMarkState, journeyDetails) ||
    markSuccessKind === ATTENDANCE_EVENT_TYPE.SHIFT_START;

  /**
   * All mark/break CTAs + timeline use the same JourneyMarkState from attendance_events
   * (plus optimistic updates after a successful mark in this drawer session).
   */
  const hasJourneyEnded = Boolean(journeyMarkState?.hasShiftEnd);
  const isOnBreak = Boolean(journeyMarkState?.isOnBreak);
  const hasBreakEnded = Boolean(journeyMarkState?.hasBreakEnd);
  const canStartBreak =
    hasJourneyStarted && !hasJourneyEnded && !isOnBreak && !hasBreakEnded;
  const canEndJourney = hasJourneyStarted && !hasJourneyEnded;
  const confirmModalOpen = confirmKind != null;

  const journeyTimeline = useMemo(() => {
    return buildAttendanceJourneyTimeline(
      journeyMarkState?.completedMarkings ?? [],
      DateUtil.getDefaultTimeZone(),
      getScheduledAtByTypeFromScheduleDetails(journeyDetails),
    );
  }, [journeyDetails, journeyMarkState]);

  return {
    step,
    search,
    setSearch,
    selectedUser,
    journeyDetails,
    journeyTimeline,
    journeyDetailsLoading,
    journeyDetailsError,
    hasJourneyStarted,
    hasJourneyEnded,
    isOnBreak,
    canStartBreak,
    canEndJourney,
    users: visibleUsers,
    usersLoading: (initialUsers ? false : usersLoading) || selectingUser,
    currentPage,
    pageSize,
    totalCount,
    handlePageChange,
    handleSelectUser,
    handleBack,
    handleStartBreak,
    handleEndBreak,
    handleEndJourney,
    confirmModalOpen,
    confirmKind,
    confirmMarkedAt,
    confirmSubmitting,
    markSuccessKind,
    handleConfirmMark,
    handleCancelConfirm,
    handleMarkedAtChange: (next: Date) => {
      setConfirmMarkedAt(clampMarkedAtToNow(next));
    },
  };
};
